// ImportWizard — AI-powered portfolio import
// Step 1: Upload file or paste text
// Step 2: Processing (AI extraction)
// Step 3: Review and select items before import

import { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import type { PropertyRecord } from "../../types/portfolio";
import type { Structure } from "../../types/entities";
import { callAiNarrative } from "../../lib/aiCall";
import { NumberInput } from "../ui/NumberInput";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExtractedProperty {
  _id: string;
  nickname: string;
  address: string;
  suburb: string;
  state: string;
  postcode: string;
  assetClass: string;
  propertyType: string;
  purchasePrice: string;
  purchaseDate: string;
  buyCosts: string;
  currentValue: string;
  weeklyRent: string;
  loanBalance: string;
  rate: string;
  lender: string;
  repaymentType: string;
  loanTermYears: string;
  annualRates: string;
  annualInsurance: string;
  annualStrata: string;
  annualLandTax: string;
  annualMaintenance: string;
  annualPropertyManagement: string;
  managementFeePercent: string;
  leaseStartDate: string;
  leaseEndDate: string;
  tenantName: string;
  ownershipPercent: string;
  rentEscalationType: string;
  rentEscalationRate: string;
  loanAccountNumber: string;
  loanStartDate: string;
  ioTermYears: string;
  propertyManagerName: string;
  propertyManagerEmail: string;
  propertyManagerPhone: string;
  bondHeld: string;
  bankValuation: string;
  entityName: string;
}

interface ExtractedStructure {
  _id: string;
  displayName: string;
  entityType: string;
  abn: string;
}

interface ExtractedLoan {
  lender: string;
  balance: string;
  rate: string;
  repaymentType: string;
  propertyAddress: string;
}

interface UnrecognisedItem {
  field: string;
  value: string;
  context: string;
}

export interface ExtractedBudgetCategory {
  category: string;
  groupName: string;
  budget: number;
  frequency: string;
}

export interface ExtractedBudgetIncome {
  label: string;
  amount: number;
  frequency: string;
}

export interface ExtractedBudget {
  categories: ExtractedBudgetCategory[];
  income: ExtractedBudgetIncome[];
}

interface AIResult {
  properties?: {
    nickname?: string;
    address?: string;
    suburb?: string;
    state?: string;
    postcode?: string;
    assetClass?: string;
    propertyType?: string;
    purchaseDate?: string | null;
    purchasePrice?: number | null;
    buyCosts?: number | null;
    currentValue?: number | null;
    weeklyRent?: number | null;
    loanBalance?: number | null;
    lender?: string | null;
    rate?: number | null;
    repaymentType?: string | null;
    loanTermYears?: number | null;
    annualRates?: number | null;
    annualInsurance?: number | null;
    annualStrata?: number | null;
    annualLandTax?: number | null;
    annualMaintenance?: number | null;
    annualPropertyManagement?: number | null;
    managementFeePercent?: number | null;
    leaseStartDate?: string | null;
    leaseEndDate?: string | null;
    tenantName?: string | null;
    ownershipPercent?: number | null;
    rentEscalationType?: string | null;
    rentEscalationRate?: number | null;
    loanAccountNumber?: string | null;
    loanStartDate?: string | null;
    ioTermYears?: number | null;
    propertyManagerName?: string | null;
    propertyManagerEmail?: string | null;
    propertyManagerPhone?: string | null;
    bondHeld?: number | null;
    bankValuation?: number | null;
    entityName?: string | null;
  }[];
  loans?: {
    lender?: string | null;
    balance?: number | null;
    rate?: number | null;
    repaymentType?: string | null;
    propertyAddress?: string | null;
  }[];
  budget?: {
    grossAnnualIncome?: number | null;
    partnerIncome?: number | null;
    monthlyLivingExpenses?: number | null;
  } | null;
  structures?: {
    displayName?: string;
    entityType?: string;
    abn?: string | null;
  }[];
  unrecognised?: {
    field?: string;
    value?: string;
    context?: string;
  }[];
}

interface ImportWizardProps {
  onImport: (properties: PropertyRecord[], structures: Omit<Structure, "id">[], updateIds: Set<string>) => Promise<{success: boolean; error?: string}>;
  onImportBudget?: (budget: ExtractedBudget, mode: 'replace' | 'merge') => void;
  onClose: () => void;
  onNavigate?: (page: string) => void;
  existingAddresses: Set<string>;
  importMode?: 'replace' | 'merge';
}

// ─── Constants ────────────────────────────────────────────────────────────────

const LOADING_MESSAGES = [
  "Reading your data...",
  "Identifying properties...",
  "Extracting loan details...",
  "Mapping to your portfolio...",
];

const SYSTEM_PROMPT = `You are a data extraction assistant for AusProp, an Australian property investment tracking app. Extract ALL portfolio data from the provided document and return ONLY a valid JSON object — no markdown, no code fences, no explanations.

Extract every number, date, name and text value that could map to a property investment record. When uncertain about a field, add it to "unrecognised" rather than dropping it.

Return this exact JSON structure:

{
  "properties": [
    {
      "nickname": "short name or nickname, or null",
      "address": "full street address or null",
      "suburb": "suburb name or null",
      "state": "NSW|VIC|QLD|SA|WA|TAS|NT|ACT or null",
      "postcode": "4-digit string or null",
      "assetClass": "Residential|Commercial|Industrial|Mixed Use or null",
      "propertyType": "House|Townhouse|Unit|Apartment|Duplex|Triplex|Villa|Acreage|Warehouse|Office|Retail|Other or null",
      "purchaseDate": "YYYY-MM-DD or null",
      "purchasePrice": number or null,
      "buyCosts": "total acquisition costs excluding purchase price, number or null",
      "currentValue": "current market value, number or null",
      "weeklyRent": "weekly rental income in dollars, number or null",
      "loanBalance": "outstanding loan balance, number or null",
      "lender": "bank or lender name, string or null",
      "rate": "interest rate as decimal percentage e.g. 6.2, number or null",
      "repaymentType": "P&I|IO or null",
      "loanTermYears": "loan term in years, number or null",
      "annualRates": "council rates per year, number or null",
      "annualInsurance": "building or landlord insurance per year, number or null",
      "annualStrata": "body corporate or strata fees per year, number or null",
      "annualLandTax": "land tax per year, number or null",
      "annualMaintenance": "maintenance and repairs per year, number or null",
      "annualPropertyManagement": "property management fees per year, number or null",
      "managementFeePercent": "management fee as % of rent e.g. 7.7, number or null",
      "leaseStartDate": "YYYY-MM-DD or null",
      "leaseEndDate": "YYYY-MM-DD or null",
      "tenantName": "current tenant name or null",
      "ownershipPercent": "ownership percentage e.g. 100, number or null",
      "rentEscalationType": "CPI|Fixed|Market or null",
      "rentEscalationRate": "e.g. 3.5 for 3.5% fixed annual increase, number or null",
      "loanAccountNumber": "loan or mortgage account number, string or null",
      "loanStartDate": "date loan was drawn down YYYY-MM-DD or null",
      "ioTermYears": "interest-only term in years e.g. 5, number or null",
      "propertyManagerName": "property manager or agent contact name, string or null",
      "propertyManagerEmail": "property manager email, string or null",
      "propertyManagerPhone": "property manager phone, string or null",
      "bondHeld": "rental bond held in dollars, number or null",
      "bankValuation": "bank or lender valuation in dollars, number or null",
      "entityName": "The name of the trust, company, SMSF or individual entity that holds this property. Match exactly to entity names found elsewhere in the document. Leave empty string if unknown."
    }
  ],
  "loans": [
    {
      "lender": "string or null",
      "balance": "outstanding balance, number or null",
      "rate": "interest rate as decimal percentage, number or null",
      "repaymentType": "P&I|IO or null",
      "propertyAddress": "address of secured property or null"
    }
  ],
  "budget": {
    "grossAnnualIncome": "primary income before tax, number or null",
    "partnerIncome": "partner or spouse income before tax, number or null",
    "monthlyLivingExpenses": "total monthly personal living costs, number or null"
  },
  "structures": [
    {
      "displayName": "entity name e.g. Smith Family Trust",
      "entityType": "Personal|Trust|Company|SMSF or null",
      "abn": "ABN as string or null"
    }
  ],
  "unrecognised": [
    {
      "field": "best guess at what this represents",
      "value": "raw value from document",
      "context": "label or surrounding text that gave context"
    }
  ]
}

Rules:
- CRITICAL: You MUST complete the entire JSON object. If you are running low on space, skip the 'unrecognised' array entirely and close all open brackets. A complete valid JSON is more important than capturing every field.
- Return ALL properties found — multiple properties = multiple array entries
- Dollar amounts: plain numbers without $ or commas e.g. 750000 not "$750,000"
- Percentages: decimal number e.g. 6.2 not "6.2%"
- Dates: YYYY-MM-DD format where possible
- Use null for any field you cannot confidently extract — do NOT invent or guess values
- loans array: only for loan data NOT already captured inside a property entry above
- unrecognised: any investment-relevant value that does not map clearly to the fields above
- If a section has no data, use an empty array or null
- IMPORTANT: Do NOT include mortgage repayments, loan repayments, loan interest, or loan principal in the annual expense fields (annualRates, annualInsurance, annualStrata, etc.). Loan costs belong in loanBalance + rate fields — the app computes interest automatically from balance × rate. Mapping mortgage repayments to expense fields causes double-counting.`;

const BUDGET_SYSTEM_PROMPT = `You are a financial data extraction assistant. Extract personal budget and income information from ANY type of financial spreadsheet or document.

Handle many formats: Barefoot Investor buckets, standard categories, YNAB, PocketBook, Mint exports, bank statements, payslips, or any other financial document.

CRITICAL — PRESERVE SOURCE FREQUENCY AND AMOUNTS:
Output each row's budget/amount in the ORIGINAL frequency as stated in the spreadsheet. Do NOT convert to monthly.

Frequency detection rules (check column headers, row labels, or document-level notes):
- "Yearly" / "Annual" / "per year" / "p.a." / "Y" → frequency: "Y"
- "Weekly" / "per week" / "W" → frequency: "W"
- "Fortnightly" / "every 2 weeks" / "per fortnight" / "F" → frequency: "F"
- "Quarterly" / "per quarter" / "Q" → frequency: "Q"
- "Monthly" / "per month" / "M" → frequency: "M"
- "BiMonthly" / "every 2 months" → convert to monthly (amount × 6 / 12) and output frequency: "M" (BiMonthly is not a supported frequency)
- If frequency is genuinely ambiguous or not stated anywhere → assume "M" (monthly)

Examples:
- Spreadsheet says "Electricity $2,470 Yearly" → { "budget": 2470, "frequency": "Y" }
- Spreadsheet says "Groceries $385 Weekly" → { "budget": 385, "frequency": "W" }
- Spreadsheet says "Childcare $800 Fortnightly" → { "budget": 800, "frequency": "F" }
- Spreadsheet says "Water $165 Quarterly" → { "budget": 165, "frequency": "Q" }
- Spreadsheet says "Internet $69" (no frequency column) → { "budget": 69, "frequency": "M" }
- Spreadsheet says "Cleaning $60 BiMonthly" → { "budget": 30, "frequency": "M" }

INCOME EXTRACTION RULES:
- Look for labels suggesting income: salary, wage, pay, income, earnings, take home, net pay, gross pay, base pay, overtime, bonus, dividends, rent received, interest, pension, centrelink, benefits, allowance
- Extract the person's name if present (e.g. "Nic (base) Take home pay" → label: "Nic take home pay")
- PRESERVE the source frequency — do NOT convert income to monthly
- If paycycle is mentioned (e.g. "Paycycle: Fortnightly"), use that frequency
- Example: $4,910 fortnightly → { "amount": 4910, "frequency": "F" }

EXPENSE EXTRACTION — AVOIDING DOUBLE-COUNTING:
- Extract ONLY the budgeted/planned amount, NOT actuals
- If a row has multiple numeric columns, use ONLY the column labelled "Budget", "Planned", "Target", or the FIRST non-zero numeric column
- IGNORE columns labelled "Actual", "Spent", "Real", "YTD", or month names (Jan, Feb, Mar, etc.)
- De-duplicate: if the same category name appears multiple times, combine into ONE entry with the summed amount
- Group items under a groupName using EXACTLY these names: "Banking & Finance", "Transport", "Household Utilities", "Health & Beauty", "Home", "Pets", "Work & Study", "Groceries", "Kids", "Insurances", "Investment Property", "Miscellaneous"
- If the document uses Barefoot bucket names (Daily/Smile/Fire Extinguisher/Mojo/Grow), use them as groupName
- PRESERVE source amounts and frequencies — do NOT convert to monthly

OUTPUT FORMAT (respond with ONLY valid JSON, no markdown, no explanation):
{
  "income": [
    { "label": "string — person name + income type", "amount": number (SOURCE amount), "frequency": "W|F|M|Q|Y" }
  ],
  "categories": [
    { "category": "string — specific expense name", "groupName": "string — broad group", "budget": number (SOURCE amount), "frequency": "W|F|M|Q|Y" }
  ]
}

If no income found, return "income": []. If no expenses found, return "categories": [].
NEVER return null. Always return the JSON object. No markdown. No explanation.`;

const XERO_SYSTEM_PROMPT = `You are extracting Australian property portfolio data from a Xero accounting software export.

Xero exports use these conventions you must understand:
- "Tracking Categories" in Xero are typically used to track individual properties — each tracking category value is usually a property address or nickname
- "Account Codes" follow a chart of accounts structure. Common patterns for property investors:
  - 200-series: Revenue/Income (rental income will appear here)
  - 400-series: Expenses (rates, insurance, management fees, repairs, interest)
  - 800-series: Assets (property values, loan offsets)
  - 900-series: Liabilities (mortgage balances)
- "Contact" field usually contains the tenant name or property manager
- Column headers may include: Date, Description, Reference, Debit, Credit, Net, Account, Account Code, Tracking Category 1, Tracking Category 2
- Rental income rows: look for accounts named "Rental Income", "Rent Received", or similar in the revenue section
- Loan/mortgage: look for liability accounts with "Mortgage", "Loan", or "ANZ/CBA/NAB/WBC" in the name
- Property management fees: look for expense accounts with "Management" or "Agent" in the name

Your job: map this accounting data back to property records. Each unique tracking category value = one property. Extract:
- Property address/nickname from tracking category
- Weekly rent = total annual rental income ÷ 52
- Loan balance from liability account balance
- Annual expenses per category (rates, insurance, management fees, maintenance) — but NOT loan interest or mortgage repayments (those are captured in loanBalance + rate fields; the app computes interest automatically)
- Entity/structure from the account name prefix if present (e.g. "Trust - Rental Income" suggests a trust structure)

Return the same JSON format as always: { properties[], structures[], loans[], budget{} }
If tracking categories aren't present, try to infer properties from account descriptions or contact names.
Never invent values — use null if uncertain.`;

const ACCEPT = ".xlsx,.xls,.csv,.pdf,.txt,.xml";
const MAX_CONTENT_CHARS = 80000;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function uid() { return crypto.randomUUID(); }

function numStr(v: number | null | undefined): string {
  if (v == null) return "";
  return String(v);
}

function strNum(v: string): number {
  const n = parseFloat(v.replace(/[^0-9.-]/g, ""));
  return isNaN(n) ? 0 : n;
}

function stripJsonFences(s: string): string {
  return s.replace(/^```(?:json)?\s*/i, "").replace(/\s*```\s*$/, "").trim();
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

async function readFileAsText(file: File): Promise<{ text: string; sheetMap: Record<string, string> }> {
  if (file.name.match(/\.(xlsx|xls)$/i)) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result as ArrayBuffer;
          const wb = XLSX.read(data, { type: "array" });
          const PRIORITY_SHEET_KEYWORDS = [
            'portfolio', 'summary', 'property', 'loan', 'equity',
            'nirimba', 'melton', 'brisbane', 'driver', 'woolner',
            'bendigo', 'smsf', 'raceview', 'albert', 'obrien',
            'manila', 'boab', 'sunderland', 'adelaide',
          ];
          const sortedNames = [
            ...wb.SheetNames.filter(n =>
              PRIORITY_SHEET_KEYWORDS.some(k => n.toLowerCase().includes(k))
            ),
            ...wb.SheetNames.filter(n =>
              !PRIORITY_SHEET_KEYWORDS.some(k => n.toLowerCase().includes(k))
            ),
          ];
          const MAX_SHEETS = 10;
          const cappedNames = sortedNames.slice(0, MAX_SHEETS);
          const PER_SHEET_CHARS = 5000;
          const sheetMap: Record<string, string> = {};
          const allText = cappedNames.map((name) => {
            const sheet = wb.Sheets[name];
            const csv = XLSX.utils.sheet_to_csv(sheet);
            // Skip effectively empty sheets (less than 50 chars of real content)
            if (csv.replace(/,|\n/g, '').trim().length < 50) return null;
            const truncated = csv.length > PER_SHEET_CHARS
              ? csv.slice(0, PER_SHEET_CHARS) + '\n...(truncated)'
              : csv;
            sheetMap[name] = truncated;
            return `[Sheet: ${name}]\n${truncated}`;
          }).filter(Boolean).join('\n\n---\n\n');
          resolve({ text: allText.slice(0, MAX_CONTENT_CHARS), sheetMap });
        } catch (err) { reject(err); }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = ((e.target?.result as string) ?? "").slice(0, MAX_CONTENT_CHARS);
      resolve({ text, sheetMap: { "Sheet 1": text } });
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

function buildPropertyRecord(ep: ExtractedProperty): PropertyRecord {
  const id = crypto.randomUUID();
  const loanTerm = strNum(ep.loanTermYears);
  const ownership = strNum(ep.ownershipPercent);
  const mgmtFee = strNum(ep.managementFeePercent);
  const escalationRate = strNum(ep.rentEscalationRate);
  return {
    id,
    nickname: ep.nickname || ep.address.split(",")[0]?.trim() || "Imported Property",
    address: ep.address || "",
    suburb: ep.suburb || "",
    state: ep.state || "",
    postcode: ep.postcode || "",
    assetClass: (ep.assetClass as PropertyRecord["assetClass"]) || "Residential",
    propertyType: (ep.propertyType as PropertyRecord["propertyType"]) || "House",
    purchaseDate: ep.purchaseDate || "",
    purchasePrice: strNum(ep.purchasePrice),
    buyCosts: strNum(ep.buyCosts),
    legalBuyCosts: 0,
    currentValue: strNum(ep.currentValue),
    weeklyRent: strNum(ep.weeklyRent),
    loanBalance: strNum(ep.loanBalance),
    structureId: "",
    owner: "",
    lender: ep.lender || "",
    loanAccountNumber: ep.loanAccountNumber || "",
    offsetAccount: "",
    loanStartDate: ep.loanStartDate || "",
    loanTermYears: loanTerm || 30,
    ioTermYears: strNum(ep.ioTermYears) || 0,
    rate: strNum(ep.rate),
    repaymentType: ep.repaymentType || "P&I",
    rateType: "Variable",
    monthlyExpenses: 0,
    annualRates: strNum(ep.annualRates),
    annualInsurance: strNum(ep.annualInsurance),
    annualStrata: strNum(ep.annualStrata),
    annualLandTax: strNum(ep.annualLandTax),
    annualMaintenance: strNum(ep.annualMaintenance),
    annualPropertyManagement: strNum(ep.annualPropertyManagement),
    personalTopUp: 0,
    estimatedSellingCostsPct: 2.5,
    customExpenses: [],
    customTabs: [],
    notes: "",
    ...(ep.leaseStartDate ? { leaseStartDate: ep.leaseStartDate } : {}),
    ...(ep.leaseEndDate ? { leaseEndDate: ep.leaseEndDate } : {}),
    ...(ep.tenantName ? { tenantName: ep.tenantName } : {}),
    ...(mgmtFee > 0 ? { managementFeePercent: mgmtFee } : {}),
    ...(ownership > 0 ? { ownershipPercent: ownership } : {}),
    ...(ep.rentEscalationType ? { rentEscalationType: ep.rentEscalationType as PropertyRecord["rentEscalationType"] } : {}),
    ...(escalationRate > 0 ? { rentEscalationRate: escalationRate } : {}),
    ...(ep.propertyManagerName ? { propertyManagerName: ep.propertyManagerName } : {}),
    ...(ep.propertyManagerEmail ? { propertyManagerEmail: ep.propertyManagerEmail } : {}),
    ...(ep.propertyManagerPhone ? { propertyManagerPhone: ep.propertyManagerPhone } : {}),
    ...(strNum(ep.bondHeld) > 0 ? { bondHeld: strNum(ep.bondHeld) } : {}),
    ...(strNum(ep.bankValuation) > 0 ? { bankValuation: strNum(ep.bankValuation) } : {}),
  };
}

function freqLabel(f: string): string {
  if (f === 'W') return 'Weekly';
  if (f === 'F') return 'Fortnightly';
  if (f === 'M') return 'Monthly';
  if (f === 'Q') return 'Quarterly';
  if (f === 'Y') return 'Yearly';
  return f;
}

function toMonthlyAmount(amount: number, freq: string): number {
  if (freq === 'W') return (amount * 52) / 12;
  if (freq === 'F') return (amount * 26) / 12;
  if (freq === 'Q') return amount / 3;
  if (freq === 'Y') return amount / 12;
  return amount; // M or unknown
}

function freqConversionNote(freq: string): string | null {
  if (freq === 'W') return 'Weekly ×4.33';
  if (freq === 'F') return 'Fortnightly ×2.17';
  if (freq === 'Q') return 'Quarterly ÷3';
  if (freq === 'Y') return 'Yearly ÷12';
  return null;
}

async function extractBudgetData(sheetMap: Record<string, string>): Promise<ExtractedBudget | null> {
  const MAX_BUDGET_CHARS = 20000;
  const BUDGET_KEYWORDS = ['income', 'expense', 'expenses', 'budget', 'dashboard', 'cashflow', 'spend', 'spending', 'saving', 'salary', 'pay', 'personal', 'finance', 'barefoot', 'daily', 'smile', 'fire', 'mojo', 'grow', 'paycycle', 'take home'];
  const EXCLUDE_SHEET_NAMES = ['capital', 'growth', 'calculator', 'trust', 'transfer', 'equity', 'loan', 'resi', 'property', 'smsf', 'structure', 'portfolio', 'strategy', 'position', 'depreciation', 'cgt'];
  const PROPERTY_CONTENT_KEYWORDS = ['purchase price', 'loan balance', 'lvr', 'address', 'suburb', 'postcode', 'weekly rent', 'settlement'];

  const allEntries = Object.entries(sheetMap);

  // Tier 1: sheets whose names match budget keywords (highest priority)
  const budgetSheets = allEntries.filter(([name]) =>
    BUDGET_KEYWORDS.some(k => name.toLowerCase().includes(k))
  );
  // Tier 2: remaining sheets that are NOT excluded by name AND not clearly property data by content
  const otherSheets = allEntries.filter(([name, csv]) =>
    !BUDGET_KEYWORDS.some(k => name.toLowerCase().includes(k)) &&
    !EXCLUDE_SHEET_NAMES.some(k => name.toLowerCase().includes(k)) &&
    !PROPERTY_CONTENT_KEYWORDS.some(k => csv.toLowerCase().slice(0, 500).includes(k))
  );
  // Combine: budget sheets first, then non-excluded sheets
  const sorted = [...budgetSheets, ...otherSheets];

  const content = sorted
    .map(([name, csv]) => `[Sheet: ${name}]\n${csv}`)
    .join('\n\n---\n\n')
    .slice(0, MAX_BUDGET_CHARS);

  console.log("[ImportWizard] budget extraction: sending", sorted.length, "sheets,", content.length, "chars (max", MAX_BUDGET_CHARS, "). Sheet names:", sorted.map(([n]) => n).join(", "));

  try {
    const raw = await callAiNarrative({
      systemPrompt: BUDGET_SYSTEM_PROMPT,
      messages: [{ role: "user", content: `Extract all personal budget data from this document:\n\n${content}` }],
      maxTokens: 3000,
    });
    console.log("[ImportWizard] budget AI response length:", raw.length, "chars");
    const parsed = JSON.parse(stripJsonFences(raw)) as {
      income?: { label?: string; amount?: number; frequency?: string }[];
      categories?: { category?: string; groupName?: string; budget?: number; frequency?: string }[];
    };
    const result: ExtractedBudget = {
      income: (parsed.income ?? []).map((i) => ({
        label: i.label ?? "",
        amount: i.amount ?? 0,
        frequency: i.frequency ?? "M",
      })),
      categories: (parsed.categories ?? []).map((c) => ({
        category: c.category ?? "",
        groupName: c.groupName ?? "Miscellaneous",
        budget: c.budget ?? 0,
        frequency: c.frequency ?? "M",
      })),
    };
    console.log("[ImportWizard] budget extraction results:", result.income.length, "income rows,", result.categories.length, "categories");
    return result;
  } catch (err) {
    console.error("[ImportWizard] budget extraction failed:", err instanceof Error ? err.message : String(err));
    return null;
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ImportWizard({ onImport, onImportBudget, onClose, onNavigate, existingAddresses, importMode = 'replace' }: ImportWizardProps) {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [importSource, setImportSource] = useState<'generic' | 'xero' | 'myob'>('generic');
  const [file, setFile] = useState<File | null>(null);
  const [pasteText, setPasteText] = useState("");
  const [dragging, setDragging] = useState(false);
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [retryText, setRetryText] = useState("");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  // Extracted data
  const [props, setProps] = useState<ExtractedProperty[]>([]);
  const [structs, setStructs] = useState<ExtractedStructure[]>([]);
  const [loans, setLoans] = useState<ExtractedLoan[]>([]);
  const [unrecognised, setUnrecognised] = useState<UnrecognisedItem[]>([]);
  // Selection state (all selected by default after extraction)
  const [selectedPropIds, setSelectedPropIds] = useState<Set<string>>(new Set());
  const [selectedStructIds, setSelectedStructIds] = useState<Set<string>>(new Set());
  const [updatePropIds, setUpdatePropIds] = useState<Set<string>>(new Set());
  const [budgetOnlyMode, setBudgetOnlyMode] = useState(false);
  // UI
  const [showUnrecognised, setShowUnrecognised] = useState(false);
  const [extractedBudget, setExtractedBudget] = useState<ExtractedBudget | null>(null);
  const [importBudget, setImportBudget] = useState<boolean>(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (step === 2) {
      intervalRef.current = setInterval(() => {
        setLoadingMsgIdx((i) => (i + 1) % LOADING_MESSAGES.length);
      }, 3500);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [step]);

  const hasInput = !!file || pasteText.trim().length > 0;

  // ── File handling ──────────────────────────────────────────────────────────

  function handleFileChange(f: File) {
    setFile(f);
    setError(null);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileChange(f);
  }

  // ── AI extraction ─────────────────────────────────────────────────────────

  async function runExtraction(content: string, sheetMap?: Record<string, string>) {
    const sm = sheetMap ?? { "Sheet 1": content };
    setStep(2);
    setLoadingMsgIdx(0);
    setError(null);
    try {
      console.log("[ImportWizard] starting extraction, content length:", content.length);
      console.log('[ImportWizard] calling AI with maxTokens:', 8000, 'content length:', content.length);
      const [raw, budgetResult] = await Promise.all([
        callAiNarrative({
          systemPrompt: importSource === 'xero' ? XERO_SYSTEM_PROMPT : importSource === 'myob' ? XERO_SYSTEM_PROMPT.replace(/Xero/g, 'MYOB').replace(/tracking categor/gi, 'job/category') : SYSTEM_PROMPT,
          messages: [{ role: "user", content: `Extract all property portfolio data from this document:\n\n${content}` }],
          maxTokens: 8000,
        }),
        extractBudgetData(sm),
      ]);
      setExtractedBudget(budgetResult);
      console.log("[ImportWizard] budgetResult:", budgetResult ? `${budgetResult.income.length} income, ${budgetResult.categories.length} categories` : "null");
      console.log("[ImportWizard] raw AI response length:", raw.length, 'last 200 chars:', raw.slice(-200));

      let parsed: AIResult;
      try {
        parsed = JSON.parse(stripJsonFences(raw)) as AIResult;
      } catch {
        console.error("[ImportWizard] JSON parse failed, raw response:", raw.slice(0, 500));
        setError("We had trouble reading the AI response. Try pasting your data as plain text below.");
        setStep(1);
        return;
      }

      const extractedProps: ExtractedProperty[] = (parsed.properties ?? []).map((p) => ({
        _id: uid(),
        nickname: p.nickname ?? "",
        address: p.address ?? "",
        suburb: p.suburb ?? "",
        state: p.state ?? "",
        postcode: p.postcode ?? "",
        assetClass: p.assetClass ?? "Residential",
        propertyType: p.propertyType ?? "House",
        currentValue: numStr(p.currentValue),
        weeklyRent: numStr(p.weeklyRent),
        loanBalance: numStr(p.loanBalance),
        rate: numStr(p.rate),
        lender: p.lender ?? "",
        repaymentType: p.repaymentType ?? "P&I",
        purchasePrice: numStr(p.purchasePrice),
        purchaseDate: p.purchaseDate ?? "",
        buyCosts: numStr(p.buyCosts),
        loanTermYears: numStr(p.loanTermYears),
        annualRates: numStr(p.annualRates),
        annualInsurance: numStr(p.annualInsurance),
        annualStrata: numStr(p.annualStrata),
        annualLandTax: numStr(p.annualLandTax),
        annualMaintenance: numStr(p.annualMaintenance),
        annualPropertyManagement: numStr(p.annualPropertyManagement),
        managementFeePercent: numStr(p.managementFeePercent),
        leaseStartDate: p.leaseStartDate ?? "",
        leaseEndDate: p.leaseEndDate ?? "",
        tenantName: p.tenantName ?? "",
        ownershipPercent: numStr(p.ownershipPercent),
        rentEscalationType: p.rentEscalationType ?? "",
        rentEscalationRate: numStr(p.rentEscalationRate),
        loanAccountNumber: p.loanAccountNumber ?? "",
        loanStartDate: p.loanStartDate ?? "",
        ioTermYears: numStr(p.ioTermYears),
        propertyManagerName: p.propertyManagerName ?? "",
        propertyManagerEmail: p.propertyManagerEmail ?? "",
        propertyManagerPhone: p.propertyManagerPhone ?? "",
        bondHeld: numStr(p.bondHeld),
        bankValuation: numStr(p.bankValuation),
        entityName: p.entityName ?? "",
      }));

      const extractedStructs: ExtractedStructure[] = (parsed.structures ?? []).map((s) => ({
        _id: uid(),
        displayName: s.displayName ?? "",
        entityType: s.entityType ?? "Trust",
        abn: s.abn ?? "",
      }));

      const extractedLoans: ExtractedLoan[] = (parsed.loans ?? []).map((l) => ({
        lender: l.lender ?? "",
        balance: numStr(l.balance),
        rate: numStr(l.rate),
        repaymentType: l.repaymentType ?? "",
        propertyAddress: l.propertyAddress ?? "",
      }));

      const extractedUnrecognised: UnrecognisedItem[] = (parsed.unrecognised ?? []).map((u) => ({
        field: u.field ?? "",
        value: u.value ?? "",
        context: u.context ?? "",
      }));

      if (extractedProps.length === 0) {
        const hasBudget = budgetResult &&
          (budgetResult.categories.length > 0 || budgetResult.income.length > 0);
        console.log("[ImportWizard] no properties found. hasBudget:", hasBudget, "budgetResult:", budgetResult);
        if (hasBudget) {
          // Budget-only mode: skip property cards, go straight to review with budget panel
          setBudgetOnlyMode(true);
          setProps([]);
          setStructs([]);
          setLoans([]);
          setUnrecognised([]);
          setSelectedPropIds(new Set());
          setSelectedStructIds(new Set());
          setShowUnrecognised(false);
          setStep(3);
          return;
        }
        setError("No properties found. If you uploaded a budget spreadsheet, try pasting the data as plain text — budget extraction works best with CSV or tab-separated data.");
        setStep(1);
        return;
      }

      setBudgetOnlyMode(false);
      setProps(extractedProps);
      setStructs(extractedStructs);
      setLoans(extractedLoans);
      setUnrecognised(extractedUnrecognised);
      // Default: all items selected
      setSelectedPropIds(new Set(extractedProps.map((p) => p._id)));
      setSelectedStructIds(new Set(extractedStructs.map((s) => s._id)));
      setShowUnrecognised(false);
      setStep(3);
    } catch (err) {
      console.error("[ImportWizard] runExtraction error:", err);
      const msg = err instanceof Error ? err.message : "Extraction failed. Please try again.";
      setError(msg);
      setStep(1);
    }
  }

  async function handleProcess() {
    try {
      let content: string;
      let sheetMap: Record<string, string>;
      const pasted = pasteText.trim();
      if (pasted) {
        content = pasted;
        sheetMap = { "Sheet 1": content };
      } else if (file) {
        const result = await readFileAsText(file);
        content = result.text;
        sheetMap = result.sheetMap;
      } else {
        setError("No content to process.");
        return;
      }
      await runExtraction(content, sheetMap);
    } catch {
      setError("Could not read the file. Try pasting the data as text instead.");
    }
  }

  async function handleRetry() {
    if (!retryText.trim()) return;
    await runExtraction(retryText.trim());
  }

  // ── Selection helpers ─────────────────────────────────────────────────────

  function toggleProp(id: string) {
    setSelectedPropIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleStruct(id: string) {
    setSelectedStructIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // ── Property card editing ─────────────────────────────────────────────────

  function updateProp(id: string, field: keyof ExtractedProperty, value: string) {
    setProps((prev) => prev.map((p) => p._id === id ? { ...p, [field]: value } : p));
  }

  function removeProp(id: string) {
    setProps((prev) => prev.filter((p) => p._id !== id));
    setSelectedPropIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
  }

  function addBlankProp() {
    const newId = uid();
    setProps((prev) => [...prev, {
      _id: newId, nickname: "", address: "", suburb: "", state: "", postcode: "",
      assetClass: "Residential", propertyType: "House", currentValue: "", weeklyRent: "",
      loanBalance: "", rate: "", lender: "", repaymentType: "P&I",
      purchasePrice: "", purchaseDate: "", buyCosts: "", loanTermYears: "",
      annualRates: "", annualInsurance: "", annualStrata: "", annualLandTax: "",
      annualMaintenance: "", annualPropertyManagement: "", managementFeePercent: "",
      leaseStartDate: "", leaseEndDate: "", tenantName: "", ownershipPercent: "",
      rentEscalationType: "", rentEscalationRate: "",
      loanAccountNumber: "", loanStartDate: "", ioTermYears: "",
      propertyManagerName: "", propertyManagerEmail: "", propertyManagerPhone: "",
      bondHeld: "", bankValuation: "", entityName: "",
    }]);
    setSelectedPropIds((prev) => new Set([...prev, newId]));
  }

  function toggleUpdate(id: string) {
    setUpdatePropIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function updateStruct(id: string, field: keyof ExtractedStructure, value: string) {
    setStructs((prev) => prev.map((s) => s._id === id ? { ...s, [field]: value } : s));
  }

  // ── Final import ──────────────────────────────────────────────────────────

  async function handleImport() {
    setImporting(true);
    setImportError(null);
    const selectedProps = props.filter((p) => selectedPropIds.has(p._id));
    const selectedStructs = structs.filter((s) => selectedStructIds.has(s._id));
    const propertyRecords = selectedProps.map((p) => {
      const record = buildPropertyRecord(p);
      // For update records, preserve the _id as the record id so App.tsx can identify them
      const base = updatePropIds.has(p._id) ? { ...record, id: p._id } : record;
      // Carry entityName through to App.tsx for structure matching (stored temporarily in owner field)
      if (p.entityName && !base.owner) {
        base.owner = `__entity:${p.entityName}`;
      }
      return base;
    });
    const structurePartials: Omit<Structure, "id">[] = selectedStructs.map((s) => ({
      entityType: s.entityType as Structure["entityType"],
      displayName: s.displayName,
      trusteeName: "",
      trusteeType: "",
      companyName: "",
      trustName: "",
      trustType: "",
      abn: s.abn || "",
      tfn: "",
    }));
    const result = await onImport(propertyRecords, structurePartials, updatePropIds);
    setImporting(false);
    if (!result.success) {
      setImportError(result.error ?? "Import failed. Please try again.");
      return;
    }
    if (importBudget && extractedBudget && onImportBudget &&
        (extractedBudget.categories.length > 0 || extractedBudget.income.length > 0)) {
      onImportBudget(extractedBudget, importMode);
    }
    setStep(4);
  }

  // ── Budget-only import ────────────────────────────────────────────────────

  async function handleBudgetOnlyImport() {
    if (!extractedBudget || !onImportBudget) return;
    onImportBudget(extractedBudget, importMode);
    setStep(4);
  }

  // ── Styles ────────────────────────────────────────────────────────────────

  const inputCls = "w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300";
  const labelCls = "block text-xs font-medium text-neutral-500 mb-0.5";

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col bg-white rounded-3xl shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-8 pt-6 pb-4 border-b border-neutral-100">
          <div>
            {step === 1 && <h2 className="text-lg font-semibold text-neutral-900">Import your portfolio data</h2>}
            {step === 2 && <h2 className="text-lg font-semibold text-neutral-900">Analysing your document</h2>}
            {step === 3 && <h2 className="text-lg font-semibold text-neutral-900">Review and select items to import</h2>}
            {step === 4 && <h2 className="text-lg font-semibold text-neutral-900">Import complete</h2>}
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6">

          {/* ── Step 1: Upload ────────────────────────────────────────────── */}
          {step === 1 && (
            <div className="space-y-5">
              <p className="text-sm text-neutral-500">
                Upload any spreadsheet, PDF, or document you use to track your properties. We'll extract every field we can find.
              </p>

              {/* Source selector */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-2">What are you importing from?</p>
                <div className="flex flex-wrap gap-2">
                  {([
                    { value: 'generic', label: '📊 Spreadsheet / CSV' },
                    { value: 'xero', label: '🟦 Xero export' },
                    { value: 'myob', label: '🟧 MYOB export' },
                  ] as const).map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setImportSource(opt.value)}
                      className={`rounded-lg px-4 py-2 text-sm transition-colors ${
                        importSource === opt.value
                          ? 'border-2 border-[#C47A2F] bg-[#FFF7ED] font-semibold text-[#C47A2F]'
                          : 'border border-neutral-200 bg-white font-normal text-neutral-700 hover:bg-neutral-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {importSource === 'xero' && (
                  <p className="mt-2 text-xs text-neutral-500">
                    Export from Xero: Reports → Profit &amp; Loss or Balance Sheet → Export as CSV. Or go to Accounting → Chart of Accounts → Export.
                  </p>
                )}
                {importSource === 'myob' && (
                  <p className="mt-2 text-xs text-neutral-500">
                    Export from MYOB: Reports → Profit &amp; Loss → Export as CSV or Excel.
                  </p>
                )}
              </div>

              {/* File drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`cursor-pointer rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
                  dragging ? "border-neutral-400 bg-neutral-50" : "border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50"
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT}
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileChange(f); }}
                />
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto mb-3 text-neutral-400">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                {file ? (
                  <div>
                    <p className="text-sm font-medium text-neutral-800">{file.name}</p>
                    <p className="mt-0.5 text-xs text-neutral-400">{formatFileSize(file.size)}</p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm font-medium text-neutral-700">Drop your file here, or click to browse</p>
                    <p className="mt-1 text-xs text-neutral-400">Excel, CSV, PDF, or plain text — any format works</p>
                  </>
                )}
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1 border-t border-neutral-200"/>
                <span className="text-xs text-neutral-400">or paste your data</span>
                <div className="flex-1 border-t border-neutral-200"/>
              </div>

              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder="Paste a table, CSV rows, or any text from your spreadsheet here…"
                rows={5}
                className="w-full rounded-xl border border-neutral-200 px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-neutral-300"
              />

              <div className="flex items-start gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2.5">
                <span className="text-sm mt-0.5">🔒</span>
                <p className="text-xs text-neutral-500 leading-relaxed">
                  Your data is sent to Anthropic's API for processing only. It is not stored by Anthropic or used to train AI models.{" "}
                  <a href="/privacy" className="underline hover:text-neutral-700">Read our Privacy Policy.</a>
                </p>
              </div>

              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 space-y-3">
                  <p className="text-sm text-red-700">{error}</p>
                  <textarea
                    value={retryText}
                    onChange={(e) => setRetryText(e.target.value)}
                    placeholder="Paste your data here to try again…"
                    rows={4}
                    className="w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm resize-none focus:outline-none"
                  />
                  <button
                    onClick={handleRetry}
                    disabled={!retryText.trim()}
                    className="rounded-lg bg-red-700 px-4 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-50 transition-colors"
                  >
                    Try again
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Step 2: Processing ───────────────────────────────────────── */}
          {step === 2 && (
            <div className="flex flex-col items-center justify-center py-16 gap-6">
              <div className="relative h-12 w-12">
                <div className="absolute inset-0 rounded-full border-4 border-neutral-100"/>
                <div className="absolute inset-0 rounded-full border-4 border-t-neutral-900 animate-spin"/>
              </div>
              <p className="text-sm font-medium text-neutral-700 min-w-[200px] text-center transition-all">
                {LOADING_MESSAGES[loadingMsgIdx]}
              </p>
              <p className="text-xs text-neutral-400">This usually takes 10–20 seconds</p>
            </div>
          )}

          {/* ── Step 3: Review ───────────────────────────────────────────── */}
          {step === 3 && (
            <div className="space-y-5">

              {/* Budget-only mode banner */}
              {budgetOnlyMode && extractedBudget && (
                <div className="space-y-4">
                  {/* Header banner */}
                  <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4">
                    <span className="text-lg mt-0.5">✅</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-emerald-800">No properties found — budget data detected</p>
                        {importMode === 'replace' ? (
                          <span className="rounded-full bg-amber-100 border border-amber-200 px-2 py-0.5 text-xs font-medium text-amber-800">Replace mode</span>
                        ) : (
                          <span className="rounded-full bg-emerald-100 border border-emerald-200 px-2 py-0.5 text-xs font-medium text-emerald-800">Merge mode</span>
                        )}
                      </div>
                      <p className="mt-0.5 text-sm text-emerald-700">
                        {importMode === 'replace'
                          ? 'Your existing budget will be replaced with the imported data.'
                          : 'New categories will be added. Existing categories will not be changed.'}
                      </p>
                    </div>
                  </div>

                  {/* Budget preview */}
                  <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-5 space-y-3">
                    <div className="flex items-center gap-2">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-neutral-500">
                        <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>
                      </svg>
                      <span className="text-sm font-semibold text-neutral-900">Personal Budget</span>
                      <span className="text-xs text-neutral-400 ml-1">
                        — {extractedBudget.income.length > 0 && `${extractedBudget.income.length} income row${extractedBudget.income.length !== 1 ? 's' : ''}`}
                        {extractedBudget.income.length > 0 && extractedBudget.categories.length > 0 && ', '}
                        {extractedBudget.categories.length > 0 && `${extractedBudget.categories.length} expense categor${extractedBudget.categories.length !== 1 ? 'ies' : 'y'}`}
                      </span>
                    </div>

                    {extractedBudget.income.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-neutral-500 mb-1.5">Income</p>
                        <div className="space-y-1">
                          {extractedBudget.income.map((inc, i) => {
                            const monthly = toMonthlyAmount(inc.amount, inc.frequency);
                            const note = freqConversionNote(inc.frequency);
                            return (
                              <div key={i} className="flex items-center justify-between text-xs text-neutral-700">
                                <span>{inc.label}</span>
                                <span className="flex items-center gap-1.5">
                                  <span className="font-medium">${Math.round(monthly).toLocaleString("en-AU")}/mo</span>
                                  {note && <span className="text-neutral-400">({note})</span>}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {extractedBudget.categories.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-neutral-500 mb-1.5">Expense categories</p>
                        <div className="space-y-1">
                          {extractedBudget.categories.slice(0, 10).map((cat, i) => {
                            const monthly = toMonthlyAmount(cat.budget, cat.frequency);
                            const note = freqConversionNote(cat.frequency);
                            return (
                              <div key={i} className="flex items-center justify-between text-xs text-neutral-700">
                                <span>{cat.category} <span className="text-neutral-400">· {cat.groupName}</span></span>
                                <span className="flex items-center gap-1.5">
                                  <span className="font-medium">${Math.round(monthly).toLocaleString("en-AU")}/mo</span>
                                  {note && <span className="text-neutral-400">({note})</span>}
                                </span>
                              </div>
                            );
                          })}
                          {extractedBudget.categories.length > 10 && (
                            <p className="text-xs text-neutral-400">...and {extractedBudget.categories.length - 10} more</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Summary + select all controls (hidden in budget-only mode) */}
              {!budgetOnlyMode && (<>
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm text-neutral-600">
                  Found{" "}
                  <span className="font-medium">{props.length} {props.length === 1 ? "property" : "properties"}</span>
                  {structs.length > 0 && (
                    <>, <span className="font-medium">{structs.length} {structs.length === 1 ? "entity" : "entities"}</span></>
                  )}
                  {loans.length > 0 && (
                    <>, <span className="font-medium">{loans.length} standalone {loans.length === 1 ? "loan" : "loans"}</span></>
                  )}
                  {unrecognised.length > 0 && (
                    <>, <span className="font-medium text-amber-700">{unrecognised.length} unrecognised {unrecognised.length === 1 ? "item" : "items"}</span></>
                  )}.{" "}
                  Select what to import.
                </p>
                <div className="flex items-center gap-3 shrink-0 text-xs text-neutral-500">
                  <button type="button" onClick={() => setSelectedPropIds(new Set(props.map((p) => p._id)))} className="hover:text-neutral-900 transition-colors">
                    Select all
                  </button>
                  <span className="text-neutral-300">·</span>
                  <button type="button" onClick={() => setSelectedPropIds(new Set())} className="hover:text-neutral-900 transition-colors">
                    Deselect all
                  </button>
                </div>
              </div>

              {/* Property cards */}
              <div className="space-y-4">
                {props.map((p, idx) => {
                  const isSelected = selectedPropIds.has(p._id);
                  const isDuplicate = existingAddresses.has((p.address ?? "").trim().toLowerCase());
                  const isUpdateMode = updatePropIds.has(p._id);

                  // Collect additional fields extracted beyond the primary display
                  const extra: { label: string; value: string }[] = [];
                  if (p.postcode) extra.push({ label: "Postcode", value: p.postcode });
                  if (p.propertyType && p.propertyType !== "House") extra.push({ label: "Property type", value: p.propertyType });
                  if (p.purchasePrice) extra.push({ label: "Purchase price", value: `$${Number(p.purchasePrice).toLocaleString("en-AU")}` });
                  if (p.purchaseDate) extra.push({ label: "Purchase date", value: p.purchaseDate });
                  if (p.buyCosts) extra.push({ label: "Buy costs", value: `$${Number(p.buyCosts).toLocaleString("en-AU")}` });
                  if (p.loanTermYears) extra.push({ label: "Loan term", value: `${p.loanTermYears} yrs` });
                  if (p.annualRates) extra.push({ label: "Rates", value: `$${Number(p.annualRates).toLocaleString("en-AU")}/yr` });
                  if (p.annualInsurance) extra.push({ label: "Insurance", value: `$${Number(p.annualInsurance).toLocaleString("en-AU")}/yr` });
                  if (p.annualStrata) extra.push({ label: "Strata", value: `$${Number(p.annualStrata).toLocaleString("en-AU")}/yr` });
                  if (p.annualLandTax) extra.push({ label: "Land tax", value: `$${Number(p.annualLandTax).toLocaleString("en-AU")}/yr` });
                  if (p.annualMaintenance) extra.push({ label: "Maintenance", value: `$${Number(p.annualMaintenance).toLocaleString("en-AU")}/yr` });
                  if (p.annualPropertyManagement) extra.push({ label: "PM fees", value: `$${Number(p.annualPropertyManagement).toLocaleString("en-AU")}/yr` });
                  if (p.managementFeePercent) extra.push({ label: "Mgmt fee", value: `${p.managementFeePercent}%` });
                  if (p.leaseStartDate) extra.push({ label: "Lease start", value: p.leaseStartDate });
                  if (p.leaseEndDate) extra.push({ label: "Lease end", value: p.leaseEndDate });
                  if (p.tenantName) extra.push({ label: "Tenant", value: p.tenantName });
                  if (p.ownershipPercent && p.ownershipPercent !== "100") extra.push({ label: "Ownership", value: `${p.ownershipPercent}%` });
                  if (p.rentEscalationType) extra.push({ label: "Rent escalation", value: p.rentEscalationType + (p.rentEscalationRate ? ` ${p.rentEscalationRate}%` : "") });

                  return (
                    <div
                      key={p._id}
                      className={`rounded-2xl border p-5 space-y-4 transition-colors ${
                        isSelected ? "border-neutral-200 bg-neutral-50" : "border-neutral-100 bg-white opacity-60"
                      }`}
                    >
                      {/* Card header: checkbox + label + duplicate badge + remove */}
                      <div className="flex items-center justify-between gap-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleProp(p._id)}
                            className="h-4 w-4 rounded border-neutral-300 accent-neutral-900"
                          />
                          <span className="text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Property {idx + 1}
                          </span>
                        </label>
                        <div className="flex items-center gap-2">
                          {isDuplicate && (
                            <>
                              <span className="rounded-full bg-yellow-100 border border-yellow-200 px-2 py-0.5 text-xs font-medium text-yellow-800">
                                Already in portfolio
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleUpdate(p._id)}
                                className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                                  isUpdateMode
                                    ? "bg-neutral-900 text-white"
                                    : "border border-neutral-300 text-neutral-600 hover:bg-neutral-100"
                                }`}
                              >
                                {isUpdateMode ? "Update existing" : "Skip"}
                              </button>
                            </>
                          )}
                          <button onClick={() => removeProp(p._id)} className="rounded-lg px-2 py-1 text-xs text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700 transition-colors">
                            Remove
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={labelCls}>Nickname</label>
                          <input className={inputCls} value={p.nickname} onChange={(e) => updateProp(p._id, "nickname", e.target.value)} placeholder="e.g. Kellyville" />
                        </div>
                        <div>
                          <label className={labelCls}>Address</label>
                          <input className={inputCls} value={p.address} onChange={(e) => updateProp(p._id, "address", e.target.value)} placeholder="42 Example St" />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className={labelCls}>Current value ($)</label>
                          <NumberInput className={inputCls} min="0" value={p.currentValue} onChange={(e) => updateProp(p._id, "currentValue", e.target.value)} placeholder="750000" />
                        </div>
                        <div>
                          <label className={labelCls}>Weekly rent ($)</label>
                          <NumberInput className={inputCls} min="0" value={p.weeklyRent} onChange={(e) => updateProp(p._id, "weeklyRent", e.target.value)} placeholder="550" />
                        </div>
                        <div>
                          <label className={labelCls}>Loan balance ($)</label>
                          <NumberInput className={inputCls} min="0" value={p.loanBalance} onChange={(e) => updateProp(p._id, "loanBalance", e.target.value)} placeholder="500000" />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className={labelCls}>Interest rate (%)</label>
                          <NumberInput className={inputCls} min="0" step="0.01" value={p.rate} onChange={(e) => updateProp(p._id, "rate", e.target.value)} placeholder="6.2" />
                        </div>
                        <div>
                          <label className={labelCls}>Lender</label>
                          <input className={inputCls} value={p.lender} onChange={(e) => updateProp(p._id, "lender", e.target.value)} placeholder="CBA, ANZ…" />
                        </div>
                        <div>
                          <label className={labelCls}>Asset class</label>
                          <select className={inputCls} value={p.assetClass} onChange={(e) => updateProp(p._id, "assetClass", e.target.value)}>
                            <option>Residential</option>
                            <option>Commercial</option>
                            <option>Industrial</option>
                            <option>Mixed Use</option>
                          </select>
                        </div>
                      </div>

                      {/* Additional extracted fields */}
                      {extra.length > 0 && (
                        <div className="rounded-xl bg-white border border-neutral-100 px-3 py-2.5">
                          <p className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-2">Also extracted</p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {extra.map((item) => (
                              <span key={item.label} className="text-xs text-neutral-600">
                                <span className="text-neutral-400">{item.label}:</span> {item.value}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <button onClick={addBlankProp} className="text-sm text-neutral-400 hover:text-neutral-700 transition-colors">
                + Add another property manually
              </button>

              {/* Entities */}
              {structs.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-widest">Entities extracted</p>
                  {structs.map((s) => {
                    const isSelected = selectedStructIds.has(s._id);
                    return (
                      <div
                        key={s._id}
                        className={`rounded-xl border p-4 transition-colors ${
                          isSelected ? "border-neutral-200 bg-neutral-50" : "border-neutral-100 bg-white opacity-60"
                        }`}
                      >
                        <label className="flex items-center gap-3 cursor-pointer mb-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleStruct(s._id)}
                            className="h-4 w-4 rounded border-neutral-300 accent-neutral-900"
                          />
                          <span className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Entity</span>
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className={labelCls}>Entity name</label>
                            <input className={inputCls} value={s.displayName} onChange={(e) => updateStruct(s._id, "displayName", e.target.value)} />
                          </div>
                          <div>
                            <label className={labelCls}>Entity type</label>
                            {/* B106 — entity_type narrowed to the 4-token union.
                                "Personal" removed (personal-name = no structure;
                                handled at property level via structure_id IS
                                NULL). Verbose trust types collapsed into the
                                "Trust" token; sub-classification moves to the
                                separate trust_type field. */}
                            <select className={inputCls} value={s.entityType} onChange={(e) => updateStruct(s._id, "entityType", e.target.value)}>
                              <option value="">— Choose entity type —</option>
                              <option value="Trust">Trust</option>
                              <option value="SMSF">SMSF</option>
                              <option value="Company">Company</option>
                              <option value="Company / Trust">Company / Trust</option>
                            </select>
                          </div>
                          {s.abn && (
                            <div>
                              <label className={labelCls}>ABN</label>
                              <input className={inputCls} value={s.abn} onChange={(e) => updateStruct(s._id, "abn", e.target.value)} />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Standalone loans (informational — not importable as separate records) */}
              {loans.length > 0 && (
                <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3">
                  <p className="text-xs font-semibold text-neutral-600 mb-1">
                    {loans.length} standalone {loans.length === 1 ? "loan" : "loans"} found
                  </p>
                  <p className="text-xs text-neutral-500 mb-2">
                    These loans couldn't be matched to a specific property above. After importing, add them manually on the Loans page.
                  </p>
                  <div className="space-y-1">
                    {loans.map((l, i) => (
                      <p key={i} className="text-xs text-neutral-600">
                        {l.lender || "Unknown lender"}{l.balance ? ` · $${Number(l.balance).toLocaleString("en-AU")}` : ""}{l.rate ? ` · ${l.rate}%` : ""}{l.repaymentType ? ` · ${l.repaymentType}` : ""}{l.propertyAddress ? ` · ${l.propertyAddress}` : ""}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Unrecognised items (collapsible) */}
              {unrecognised.length > 0 && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowUnrecognised((v) => !v)}
                    className="flex items-center gap-2 text-xs font-medium text-amber-700 hover:text-amber-900 transition-colors"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d={showUnrecognised ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"}/>
                    </svg>
                    {showUnrecognised ? "Hide" : "Show"} {unrecognised.length} unrecognised {unrecognised.length === 1 ? "item" : "items"} — will be skipped
                  </button>
                  {showUnrecognised && (
                    <div className="mt-2 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 space-y-2">
                      {unrecognised.map((u, i) => (
                        <div key={i} className="text-xs">
                          <span className="font-medium text-amber-800">{u.field || "Unknown"}:</span>{" "}
                          <span className="text-amber-700">{u.value}</span>
                          {u.context && <span className="text-amber-500 ml-1">({u.context})</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Extracted Budget ──────────────────────────────────────── */}
              {console.log("[ImportWizard] budget panel render check:", extractedBudget ? `${extractedBudget.income.length} income, ${extractedBudget.categories.length} categories` : "null")}
              {extractedBudget !== null && (extractedBudget.categories.length > 0 || extractedBudget.income.length > 0) && (
                <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-5 space-y-3">
                  {/* Header */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-neutral-500">
                        <rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>
                      </svg>
                      <span className="text-sm font-semibold text-neutral-900">Personal Budget</span>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={importBudget}
                        onChange={(e) => setImportBudget(e.target.checked)}
                        className="h-4 w-4 rounded border-neutral-300 accent-neutral-900"
                      />
                      <span className="text-xs text-neutral-500">Replace my existing budget</span>
                    </label>
                  </div>

                  {/* Summary */}
                  <p className="text-xs text-neutral-500">
                    Found{" "}
                    <span className="font-medium text-neutral-700">
                      {extractedBudget.categories.length} expense {extractedBudget.categories.length === 1 ? "category" : "categories"}
                    </span>
                    {extractedBudget.income.length > 0 && (
                      <> and{" "}
                        <span className="font-medium text-neutral-700">
                          {extractedBudget.income.length} income {extractedBudget.income.length === 1 ? "source" : "sources"}
                        </span>
                      </>
                    )}
                  </p>

                  {/* Income rows */}
                  {extractedBudget.income.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-neutral-500 mb-1.5">Income</p>
                      <div className="space-y-1">
                        {extractedBudget.income.map((inc, i) => (
                          <div key={i} className="flex items-center justify-between text-xs text-neutral-700">
                            <span>{inc.label}</span>
                            <span className="text-neutral-500">${inc.amount.toLocaleString("en-AU")} / {freqLabel(inc.frequency)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Category preview */}
                  {extractedBudget.categories.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-neutral-500 mb-1.5">Expense categories</p>
                      <div className="space-y-1">
                        {extractedBudget.categories.slice(0, 8).map((cat, i) => (
                          <div key={i} className="flex items-center justify-between text-xs text-neutral-700">
                            <span>
                              {cat.category}{" "}
                              <span className="text-neutral-400">· {cat.groupName}</span>
                            </span>
                            <span className="text-neutral-500">${cat.budget.toLocaleString("en-AU")} / {freqLabel(cat.frequency)}</span>
                          </div>
                        ))}
                        {extractedBudget.categories.length > 8 && (
                          <p className="text-xs text-neutral-400">...and {extractedBudget.categories.length - 8} more</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              </>)}

            </div>
          )}

          {/* ── Step 4: Success ───────────────────────────────────────────── */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 rounded-2xl bg-emerald-50 border border-emerald-200 px-5 py-4">
                <span className="text-2xl">✅</span>
                <div>
                  <p className="font-semibold text-emerald-800">Properties imported successfully</p>
                  <p className="text-sm text-emerald-700 mt-0.5">Your portfolio data has been saved to your account.</p>
                </div>
              </div>
              <div className="rounded-2xl border border-neutral-200 bg-neutral-50 px-5 py-4">
                <p className="text-sm font-semibold text-neutral-800 mb-3">Complete your portfolio</p>
                <ul className="space-y-2.5">
                  {[
                    "Review each property and fill in any missing fields (purchase date, loan details)",
                    "Add your loan details in the Loans tab",
                    "Set up your Personal Budget",
                    "Fill in My Profile (income, tax) for accurate cashflow calculations",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-neutral-700">
                      <span className="mt-0.5 flex-shrink-0 w-4 h-4 rounded-full border border-neutral-300 bg-white" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-8 py-5 border-t border-neutral-100">
          {step === 1 && (
            <>
              <button onClick={onClose} className="text-sm text-neutral-400 hover:text-neutral-600 transition-colors">Cancel</button>
              <button
                onClick={handleProcess}
                disabled={!hasInput}
                className="rounded-full bg-neutral-900 px-6 py-2.5 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-40 transition-colors"
              >
                Analyse →
              </button>
            </>
          )}
          {step === 2 && (
            <button onClick={() => setStep(1)} className="text-sm text-neutral-400 hover:text-neutral-600 transition-colors">Cancel</button>
          )}
          {step === 3 && (
            <>
              {importError && (
                <div className="flex-1 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {importError}
                </div>
              )}
              <button onClick={() => setStep(1)} disabled={importing} className="text-sm text-neutral-400 hover:text-neutral-600 transition-colors disabled:opacity-40">← Start over</button>
              {budgetOnlyMode ? (
                <button
                  onClick={() => { void handleBudgetOnlyImport(); }}
                  disabled={!importBudget}
                  className="rounded-full bg-neutral-900 px-6 py-2.5 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-40 transition-colors"
                >
                  Import Budget
                </button>
              ) : (
                <button
                  onClick={() => { void handleImport(); }}
                  disabled={selectedPropIds.size === 0 || importing}
                  className="rounded-full bg-neutral-900 px-6 py-2.5 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-40 transition-colors"
                >
                  {importing ? "Saving…" : `Import ${selectedPropIds.size} ${selectedPropIds.size === 1 ? "property" : "properties"}${selectedStructIds.size > 0 ? ` + ${selectedStructIds.size} ${selectedStructIds.size === 1 ? "entity" : "entities"}` : ""}`}
                </button>
              )}
            </>
          )}
          {step === 4 && (
            <>
              <button onClick={onClose} className="text-sm text-neutral-400 hover:text-neutral-600 transition-colors">
                Skip for now
              </button>
              <button
                onClick={() => {
                  if (onNavigate) {
                    onNavigate("portfolio");
                  } else {
                    onClose();
                  }
                }}
                className="rounded-full bg-neutral-900 px-6 py-2.5 text-sm font-medium text-white hover:bg-neutral-700 transition-colors"
              >
                Go to Portfolio →
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
